/**
 * AccessPane — the READ half of access, on every detail page.
 *
 * The shape is fixed, and it is the same on a matter, a project, a task, a
 * contact, a data source, a playbook and a table:
 *
 *     rail pill "Access" (badge = count)
 *       → this pane: the owner, then everyone else, read-only
 *         → ONE footer button → the manage side panel (write)
 *
 * WHY ONE BUTTON. Before this, the same list was reachable four ways on the
 * same page — an overflow-menu "Manage Access", an "N members" button in the
 * context pane, the header avatar stack, and the pane's own button — three of
 * which skipped the reading entirely and threw a 50%-wide transactional
 * editor over the page. The stack and the context pane now RAISE THIS PANE;
 * only this button opens the editor.
 *
 * The button is always present, even for someone who cannot change anything:
 * "View access" opens the same panel with its add/remove controls suppressed,
 * which is how a viewer finds out whom to ask.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../TailwindComponents/Button';
import PaneFooter from '../surface/PaneFooter';
import AccessList from './AccessList';
import { AccessPerson, normaliseAll, ownerFirst } from './accessModel';

export interface AccessPaneProps {
  /** Raw rows, in whatever shape the endpoint sends — normalised here. */
  collaborators: any[];
  /** Opens the shared `manageResourceCollaborators` side panel. */
  onManageAccess: () => void;
  /** Labels the footer button: "Manage access" vs "View access". */
  canManage: boolean;
  /**
   * Pin the owner/lead to the top under their own heading.
   *
   * On by default: "whose record is this" is the first thing the list is read
   * for, and it is the one row that cannot be removed.
   */
  pinOwner?: boolean;
  /** "Owner" everywhere, "Lead" on a contact. */
  ownerLabel?: string;
  /** Above the list — pending access requests, a scope note. */
  header?: React.ReactNode;
  emptyText?: string;
  renderRowExtra?: (person: AccessPerson) => React.ReactNode;
  /** Anchor for a feature tour, carried over from whatever this replaced. */
  tourId?: string;
}

const AccessPane: React.FC<AccessPaneProps> = ({
  collaborators,
  onManageAccess,
  canManage,
  pinOwner = true,
  ownerLabel,
  header,
  emptyText,
  renderRowExtra,
  tourId,
}) => {
  const { t } = useTranslation();

  const people = useMemo(() => ownerFirst(normaliseAll(collaborators)), [collaborators]);
  const owner = pinOwner ? people.find((p) => p.isOwner || p.isLead) : undefined;
  const rest = owner ? people.filter((p) => p !== owner) : people;

  return (
    <div className="flex flex-col h-full min-h-0" data-tour-id={tourId}>
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-2 space-y-3">
        {header}

        {owner && (
          <div>
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-fg-muted mb-1.5">
              {t(ownerLabel || 'Owner')}
            </h4>
            <AccessList
              people={[owner]}
              ownerLabel={ownerLabel}
              renderRowExtra={renderRowExtra}
            />
          </div>
        )}

        <div>
          {owner && rest.length > 0 && (
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-fg-muted mb-1.5">
              {t('Shared with')}
            </h4>
          )}
          {/* The empty state belongs to the WHOLE pane, so it is suppressed
              here when an owner row is already showing — "no one has access"
              under a pinned owner would be a contradiction. */}
          {(!owner || rest.length > 0) && (
            <AccessList
              people={rest}
              ownerLabel={ownerLabel}
              emptyText={emptyText}
              renderRowExtra={renderRowExtra}
            />
          )}
        </div>
      </div>

      {/* THE footer pattern, and the pane it was taken from — stat left,
          action right. See `shared/surface/PaneFooter`. */}
      <PaneFooter count={people.length} label={people.length === 1 ? t('person') : t('people')}>
        <Button
          variant="outline"
          size="small"
          onClick={onManageAccess}
          data-testid="access-manage-button"
        >
          {canManage ? t('Manage access') : t('View access')}
        </Button>
      </PaneFooter>
    </div>
  );
};

export default AccessPane;
